import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, authApi, onSessionLost, setAccessToken } from './api';
import type { AuthUser, Portal } from './types';

interface AuthState {
  user: AuthUser | null;
  /** True until the initial refresh attempt settles, so nothing flashes the login form. */
  isBootstrapping: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ portal, children }: { portal: Portal; children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isBootstrapping, setBootstrapping] = useState(true);
  const queryClient = useQueryClient();

  // A session that the API says is over (refresh cookie expired, revoked by an
  // administrator) ends here too, rather than leaving the shell up with every
  // query failing 401 twice.
  useEffect(() => {
    onSessionLost(() => {
      setAccessToken(null);
      setUser(null);
      queryClient.clear();
    });
    return () => onSessionLost(null);
  }, [queryClient]);

  // On load the access token is gone (it only ever lived in memory), but the
  // refresh cookie may still be valid — so a reload silently restores the session.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const result = await authApi.refresh();
        if (cancelled) return;

        // A refresh cookie from the other portal must not sign you in here.
        if (result.user.portal !== portal) {
          setAccessToken(null);
          queryClient.clear();
        } else {
          setAccessToken(result.accessToken);
          setUser(result.user);
        }
      } catch {
        setAccessToken(null);
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();

    return () => { cancelled = true; };
  }, [portal, queryClient]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await authApi.login(email, password, portal);
    queryClient.clear();
    setAccessToken(result.accessToken);
    setUser(result.user);
  }, [portal, queryClient]);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Already invalid server-side is a fine outcome for signing out.
      if (!(error instanceof ApiError)) throw error;
    } finally {
      setAccessToken(null);
      setUser(null);
      // Everything cached belongs to the account that just left. Query keys carry
      // no tenant, so the next sign-in on this browser would otherwise render the
      // previous tenant's inbox from cache for its first thirty seconds.
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({ user, isBootstrapping, signIn, signOut }),
    [user, isBootstrapping, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
